import { ChevronDown, Download, FileSpreadsheet, FileText } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select } from '@/components/ui/select'
import { downloadBlob, toCsv, toXlsx, XLSX_MIME, type SheetCell } from '@/lib/sheet'
import { usePeriod } from '../api'
import { PERIODS, type Period } from '../types'
import { ReportSkeleton, type TableSpec } from './ReportBits'

type Props = {
  name: string
  title: string
  description: string
  query: { isError: boolean; error: unknown; refetch: () => unknown; data?: unknown }
  sheets?: () => { title: string; spec: TableSpec }[]
  children: ReactNode
}

export function ReportLayout({ name, title, description, query, sheets, children }: Props) {
  const { t } = useTranslation()
  const { period, setPeriod } = usePeriod()

  const exportAs = (kind: 'xlsx' | 'csv') => {
    if (!sheets) return
    const rows: SheetCell[][] = [[title], [t('reports.period.label'), t(`reports.period.${period}`)], []]
    for (const s of sheets()) rows.push([s.title], s.spec.head, ...s.spec.rows, [])
    const file = `rapor-${name}-${new Date().toISOString().slice(0, 10)}`
    if (kind === 'xlsx') downloadBlob(`${file}.xlsx`, toXlsx(rows, title.slice(0, 30)), XLSX_MIME)
    else downloadBlob(`${file}.csv`, toCsv(rows), 'text/csv')
  }

  return (
    <div>
      <PageHeader
        eyebrow={t('nav.groups.insights')}
        title={title}
        description={description}
        actions={
          <>
            <Select className="w-40" value={period} onChange={(v) => setPeriod(v as Period)} aria-label={t('reports.period.label')} options={PERIODS.map((p) => ({ value: p, label: t(`reports.period.${p}`) }))} />
            {sheets ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={!query.data}>
                    <Download />
                    {t('reports.export.label')}
                    <ChevronDown className="opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-48">
                  <DropdownMenuLabel>{t('reports.export.title')}</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => exportAs('xlsx')}>
                    <FileSpreadsheet />
                    {t('reports.export.xlsx')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => exportAs('csv')}>
                    <FileText />
                    {t('reports.export.csv')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />
      {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : !query.data ? <ReportSkeleton /> : <div className="space-y-4">{children}</div>}
    </div>
  )
}
